import type { TableSortEvent } from '@app/shared/vue/components/sf-table/types'
import type {
  SortableUserColumn,
  UserLastLoginFilter,
  UserList,
  UserRole,
  UserRow,
  UserSortDirection,
  UserStatus,
} from '@app/application/user-management/types'

import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import {
  isSortableUserColumn,
  isUserLastLoginFilter,
  isUserRole,
  isUserStatus,
  userColumns,
  userLists,
  userSortDirections,
} from '@app/application/user-management/types'
import {
  FIRST_PAGE,
  QUERY_KEYS,
  STALE_USERS_REQUEST_REASON,
  USERS_REQUEST_KEY,
} from '@app/application/user-management/constants'
import { abort, getSignal } from '@app/adapters/abort'
import { fetchUsers } from '@app/application/user-management/api'
import { SfLog } from '@app/adapters/logger'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'

type QueryParam = string | (string | null)[]

const toSortBy = (param: QueryParam): SortableUserColumn =>
  typeof param === 'string' && isSortableUserColumn(param)
    ? param
    : userColumns.NAME

const toSortDirection = (param: QueryParam): UserSortDirection =>
  param === userSortDirections.DESC
    ? userSortDirections.DESC
    : userSortDirections.ASC

const toPage = (param: QueryParam): number => {
  const page = Number(param)

  return Number.isInteger(page) && page > 0 ? page : FIRST_PAGE
}

const toSearch = (param: QueryParam): string | undefined =>
  typeof param === 'string' ? param.trim() || undefined : undefined

const toRole = (param: QueryParam): UserRole | undefined =>
  typeof param === 'string' && isUserRole(param) ? param : undefined

const toStatus = (param: QueryParam): UserStatus | undefined =>
  typeof param === 'string' && isUserStatus(param) ? param : undefined

const toLastLogin = (param: QueryParam): UserLastLoginFilter | undefined =>
  typeof param === 'string' && isUserLastLoginFilter(param) ? param : undefined

// A filter with no value leaves the query string, which is how "All" is
// expressed: the absence of the filter rather than a value of its own.
const toParam = (value: string | undefined): string => value ?? ''

// The list is one page with one table on it, so a store holds what the table,
// the toolbar and the empty state all read. useUrlStorage registers its
// popstate listener on the component that first reaches the store, which is
// the list itself.
export const useUsersListStore = defineStore('usersList', () => {
  const list = ref<UserList>(userLists.STAFF)

  const sortBy = useUrlStorage<SortableUserColumn>(
    QUERY_KEYS.SORT_BY,
    userColumns.NAME,
    String,
    { deserialize: toSortBy }
  )
  const sortDirection = useUrlStorage<UserSortDirection>(
    QUERY_KEYS.SORT_DIRECTION,
    userSortDirections.ASC,
    String,
    { deserialize: toSortDirection }
  )
  const page = useUrlStorage<number>(QUERY_KEYS.PAGE, FIRST_PAGE, String, {
    deserialize: toPage,
  })
  const search = useUrlStorage<string | undefined>(
    QUERY_KEYS.SEARCH,
    undefined,
    toParam,
    { deserialize: toSearch }
  )
  const role = useUrlStorage<UserRole | undefined>(
    QUERY_KEYS.ROLE,
    undefined,
    toParam,
    { deserialize: toRole }
  )
  const status = useUrlStorage<UserStatus | undefined>(
    QUERY_KEYS.STATUS,
    undefined,
    toParam,
    { deserialize: toStatus }
  )
  const lastLogin = useUrlStorage<UserLastLoginFilter | undefined>(
    QUERY_KEYS.LAST_LOGIN,
    undefined,
    toParam,
    { deserialize: toLastLogin }
  )

  const users = ref<UserRow[]>([])
  const totalPages = ref(FIRST_PAGE)
  const loading = ref(true)
  const error = ref(false)

  const isFiltered = computed(() =>
    Boolean(search.value || role.value || status.value || lastLogin.value)
  )

  const setSort = (sort: TableSortEvent): void => {
    if (!isSortableUserColumn(sort.sortBy)) {
      return
    }

    sortBy.value = sort.sortBy
    sortDirection.value = toSortDirection(sort.sortDirection)
    // The rows the old page held are not the rows this sort puts there.
    page.value = FIRST_PAGE
  }

  const setPage = (next: number): void => {
    page.value = next
  }

  // A filter answers with a list of its own, so the page the reader was on says
  // nothing about the one they are now looking at.
  const setSearch = (term: string): void => {
    search.value = term.trim() || undefined
    page.value = FIRST_PAGE
  }

  const setRole = (value: string): void => {
    role.value = isUserRole(value) ? value : undefined
    page.value = FIRST_PAGE
  }

  const setStatus = (value: string): void => {
    status.value = isUserStatus(value) ? value : undefined
    page.value = FIRST_PAGE
  }

  const setLastLogin = (value: string): void => {
    lastLogin.value = isUserLastLoginFilter(value) ? value : undefined
    page.value = FIRST_PAGE
  }

  const clearFilters = (): void => {
    search.value = undefined
    role.value = undefined
    status.value = undefined
    lastLogin.value = undefined
    page.value = FIRST_PAGE
  }

  const loadUsers = async (): Promise<void> => {
    abort(USERS_REQUEST_KEY, STALE_USERS_REQUEST_REASON)

    // Two clicks in a row leave two requests in flight and they can answer in
    // either order. The abort above cancels the older one, and its signal is
    // how that run tells the table has moved on without it.
    const signal = getSignal(USERS_REQUEST_KEY)
    const isStale = (): boolean => signal?.aborted === true

    loading.value = true
    error.value = false

    try {
      const response = await fetchUsers({
        list: list.value,
        page: page.value,
        sortBy: sortBy.value,
        sortDirection: sortDirection.value,
        search: search.value,
        role: role.value,
        status: status.value,
        lastLogin: lastLogin.value,
      })

      if (isStale()) {
        return
      }

      users.value = response?.users ?? []
      totalPages.value = response?.pagination?.totalPages ?? FIRST_PAGE

      // A bookmarked page past the end answers with no rows and the page it was
      // asked for, so the list holds on to the last page it does have.
      if (page.value > totalPages.value) {
        setPage(totalPages.value)
      }
    } catch (e) {
      if (isStale()) {
        return
      }

      SfLog.warn('Failed to load the users list', { extra: { error: e } })

      // The page count stays as it was, so the pager is still there to retry with.
      error.value = true
    } finally {
      if (!isStale()) {
        loading.value = false
      }
    }
  }

  const initialize = (target: UserList): void => {
    list.value = target

    void loadUsers()
  }

  watch(
    [sortBy, sortDirection, page, search, role, status, lastLogin],
    loadUsers
  )

  // Everything readable leaves as a computed and everything writable as an
  // action, so no consumer can put the store somewhere loadUsers would not.
  return {
    clearFilters,
    error: computed(() => error.value),
    initialize,
    isFiltered,
    lastLogin: computed(() => lastLogin.value),
    list: computed(() => list.value),
    loading: computed(() => loading.value),
    page: computed(() => page.value),
    role: computed(() => role.value),
    search: computed(() => search.value),
    setLastLogin,
    setPage,
    setRole,
    setSearch,
    setSort,
    setStatus,
    sortBy: computed(() => sortBy.value),
    sortDirection: computed(() => sortDirection.value),
    status: computed(() => status.value),
    totalPages: computed(() => totalPages.value),
    users: computed(() => users.value),
  }
})
